import React from "react";
import { motion } from "framer-motion";
import { Crown, Timer, ExternalLink, Rocket } from "lucide-react";
import { fmtCountdown, fmtNum } from "../lib/api";

export const LeaderCard = ({ leader }) => {
  const coin = leader.coin;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 24 }}
      data-testid="leader-card"
      className="leader-glow relative rounded-3xl border-2 border-amber-400/70 bg-gradient-to-b from-amber-500/10 via-[#0c0f17]/90 to-[#0c0f17] overflow-hidden"
    >
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-72 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />

      {/* badge */}
      <div className="relative z-10 flex justify-center pt-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 text-black font-display font-black uppercase tracking-wider text-sm px-5 py-2 shadow-[0_0_30px_rgba(255,208,40,0.5)]">
          <Crown size={16} strokeWidth={2.6} /> SEND IT LEADER
        </div>
      </div>

      <div className="relative z-10 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
        <div className="relative shrink-0">
          <div className="absolute inset-0 rounded-full blur-xl bg-amber-400/60" />
          <img
            src={coin.image}
            alt={coin.name}
            onError={(e) => {
              e.currentTarget.src = `https://placehold.co/160x160/0c0f17/ffd028?text=${coin.symbol.slice(
                0,
                3
              )}`;
            }}
            className="relative h-28 w-28 sm:h-32 sm:w-32 rounded-full object-cover border-4 border-amber-400/70"
          />
          <span className="absolute -bottom-2 -right-2 grid place-items-center h-10 w-10 rounded-full bg-amber-400 text-black">
            <Rocket size={18} strokeWidth={2.6} />
          </span>
        </div>

        <div className="flex-1 text-center sm:text-left min-w-0">
          <div className="font-display font-black text-3xl sm:text-4xl truncate">{coin.name}</div>
          <div className="font-mono-num font-bold text-amber-300 tracking-wider text-lg">
            ${coin.symbol}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-x-6 gap-y-2">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                Community Votes
              </div>
              <div className="font-mono-num font-black text-2xl">{leader.votes.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                Vote Share
              </div>
              <div className="font-mono-num font-black text-2xl text-amber-300">{leader.share}%</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                24h Volume
              </div>
              <div className="font-mono-num font-black text-2xl">${fmtNum(coin.volume24h)}</div>
            </div>
          </div>
        </div>

        {/* pinned countdown */}
        <div className="shrink-0 text-center rounded-2xl border border-amber-400/30 bg-amber-400/5 px-5 py-4">
          <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-300/80 font-semibold">
            <Timer size={13} /> Pinned for
          </div>
          <div
            data-testid="leader-countdown"
            className="font-mono-num font-black text-3xl text-amber-300 mt-1"
          >
            {fmtCountdown(leader.seconds_left)}
          </div>
          {coin.url && (
            <a
              href={coin.url}
              target="_blank"
              rel="noreferrer"
              data-testid="leader-chart-link"
              className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-amber-300 transition-colors"
            >
              View chart <ExternalLink size={11} />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};
